import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import SemesterSettings from './general/SemesterSettings';
import AssignmentLibrary from './general/AssignmentLibrary';
import Courses from './general/Courses';
import AccessControl from './general/AccessControl';
import GeneralSelfEvalQuestions from './general/GeneralSelfEvalQuestions';
import GeneralSyllabus from './general/GeneralSyllabus';
import GeneralAiPermissions from './general/GeneralAiPermissions';
import InstructorInformation from './general/InstructorInformation';
import StudentManagement from './StudentManagement';

const TABS = [
  { key: 'semester', label: 'Semester' },
  { key: 'assignments', label: 'Assignment Library' },
  { key: 'courses', label: 'Courses' },
  { key: 'syllabus', label: 'Syllabus' },
  { key: 'ai', label: 'AI Permissions' },
  { key: 'selfeval', label: 'Self-Evaluation Questions' },
  { key: 'instructors', label: 'Instructor Information' },
  { key: 'access', label: 'Access', superadminOnly: true },
  { key: 'students', label: 'Students' },
];

export default function GeneralSettings({ session, onConfigure }) {
  const location = useLocation();
  const navigate = useNavigate();
  const visibleTabs = useMemo(
    () => TABS.filter(tab => !tab.superadminOnly || session.isSuperadmin),
    [session.isSuperadmin]
  );

  const [activeTab, setActiveTab] = useState(() => {
    const requested =
      new URLSearchParams(
        window.location.search
      ).get('tab');

    const valid =
      TABS.some(
        tab =>
          tab.key === requested &&
          (
            !tab.superadminOnly ||
            session.isSuperadmin
          )
      );

    return valid
      ? requested
      : session.isSuperadmin
        ? 'semester'
        : 'courses';
  });

  useEffect(() => {
    if (!visibleTabs.some(tab => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key || 'courses');
    }
  }, [activeTab, visibleTabs]);

  // GENERAL_SETTINGS_REFRESH_TAB_SYNC
  useEffect(() => {
    const params =
      new URLSearchParams(
        location.search
      );

    const requested =
      params.get('tab');

    if (!requested) {
      return;
    }

    const valid =
      visibleTabs.some(
        tab =>
          tab.key === requested
      );

    if (
      valid &&
      requested !== activeTab
    ) {
      setActiveTab(requested);
      return;
    }

    if (!valid) {
      const fallback =
        visibleTabs[0]?.key ||
        'courses';

      if (activeTab !== fallback) {
        setActiveTab(fallback);
      }

      params.set(
        'tab',
        fallback
      );

      navigate(
        `${location.pathname}?${params.toString()}`,
        { replace: true }
      );
    }
  }, [
    activeTab,
    visibleTabs,
    location.pathname,
    location.search,
    navigate
  ]);

  function selectRefreshSafeGeneralTab(
    nextTab
  ) {
    setActiveTab(nextTab);

    const params =
      new URLSearchParams(
        location.search
      );

    params.set(
      'tab',
      nextTab
    );

    navigate(
      `${location.pathname}?${params.toString()}`,
      { replace: true }
    );
  }

  return (
    <div>
      <h1 style={styles.heading}>General Settings</h1>
      <div style={styles.tabs}>
        {visibleTabs.map(tab => (
          <button
            key={tab.key}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {})
            }}
            onClick={() =>
              selectRefreshSafeGeneralTab(
                tab.key
              )
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div style={styles.content}>
        {activeTab === 'semester' && (
  <SemesterSettings session={session} />
)}
        {activeTab === 'assignments' && <AssignmentLibrary session={session} />}
        {activeTab === 'courses' && (
          <Courses session={session} onConfigure={onConfigure} />
        )}
        {activeTab === 'selfeval' && (
          <GeneralSelfEvalQuestions session={session} />
        )}
        {activeTab === 'syllabus' && <GeneralSyllabus session={session} />}
        {activeTab === 'ai' && <GeneralAiPermissions session={session} />}
        {activeTab === 'instructors' && (
          <InstructorInformation session={session} />
        )}
        {activeTab === 'access' && session.isSuperadmin && (
          <AccessControl session={session} />
        )}
        {activeTab === 'students' && (
  <StudentManagement session={session} />
)}
      </div>
    </div>
  );
}

const styles = {
  heading: {
    margin: '0 0 20px',
    fontSize: '24px',
    color: '#1f2933'
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    borderBottom: '2px solid #d7dee5',
    marginBottom: '24px',
    flexWrap: 'wrap'
  },
  tab: {
    border: 'none',
    background: 'transparent',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#66788a',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px'
  },
  tabActive: {
    color: '#215b7d',
    borderBottom: '2px solid #215b7d'
  },
  content: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #d7dee5',
    boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
  }
};